import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@services/firebase';
import { Eye, EyeOff, Trash2, RefreshCw, Plus, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const PLATFORM_COLORS = {
  instagram: 'bg-pink-100 text-pink-700',
  twitter:   'bg-sky-100 text-sky-700',
  linkedin:  'bg-blue-100 text-blue-700',
  facebook:  'bg-indigo-100 text-indigo-700',
  tiktok:    'bg-gray-100 text-gray-700',
};

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-700',
  scheduled: 'bg-yellow-100 text-yellow-700',
  draft:     'bg-gray-100 text-gray-500',
};

export default function AdminSocial() {
  const navigate = useNavigate();
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'social_posts'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() ?? new Date(0);
        const bDate = b.createdAt?.toDate?.() ?? new Date(0);
        return bDate - aDate;
      });
      setPosts(list);
    } catch {
      toast.error('Failed to load social posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const toggleVisible = async (id, current) => {
    try {
      await updateDoc(doc(db, 'social_posts', id), { visible: !current });
      setPosts(prev => prev.map(p => p.id === id ? { ...p, visible: !current } : p));
      toast.success(current ? 'Post hidden' : 'Post visible');
    } catch {
      toast.error('Failed to update post');
    }
  };

  const deletePost = async (id, content) => {
    const preview = content ? content.substring(0, 40) + '…' : 'this post';
    if (!window.confirm(`Delete "${preview}"?`)) return;
    try {
      await deleteDoc(doc(db, 'social_posts', id));
      setPosts(prev => prev.filter(p => p.id !== id));
      toast.success('Post deleted');
    } catch {
      toast.error('Failed to delete post');
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Social Posts</h1>
          <p className="text-gray-500 text-sm mt-1">{posts.length} total posts</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPosts}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={15} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => navigate('/admin/social/new')}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} />
            <span className="hidden sm:inline">New Post</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Loading / empty */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
          <p>No social posts yet.</p>
          <button
            onClick={() => navigate('/admin/social/new')}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} /> Create your first post
          </button>
        </div>
      ) : (
        <>
          {/* ── Mobile card list (hidden on md+) ── */}
          <div className="md:hidden space-y-3">
            {posts.map(post => {
              const scheduledDate = post.scheduledAt?.toDate?.();
              const isVisible = post.visible !== false;
              const status = post.status || 'draft';
              const platform = post.platform || 'other';
              return (
                <div key={post.id} className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
                  {/* Content preview */}
                  <p className="text-sm font-medium text-gray-800 line-clamp-2">
                    {post.content || '(no content)'}
                  </p>
                  {post.hashtags?.length > 0 && (
                    <p className="text-xs text-blue-400 truncate">
                      {post.hashtags.map(h => `#${h}`).join(' ')}
                    </p>
                  )}

                  {/* Badges row */}
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${PLATFORM_COLORS[platform] ?? 'bg-gray-100 text-gray-600'}`}>
                      {platform}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {status}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                      {isVisible ? 'Visible' : 'Hidden'}
                    </span>
                    {scheduledDate && (
                      <span className="text-xs text-gray-400">{format(scheduledDate, 'MMM d, yyyy HH:mm')}</span>
                    )}
                  </div>

                  {/* Actions row */}
                  <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/admin/social/${post.id}/edit`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-blue-600 bg-blue-50 text-xs font-medium hover:bg-blue-100 transition-colors"
                    >
                      <Pencil size={13} /> Edit
                    </button>
                    <button
                      onClick={() => toggleVisible(post.id, isVisible)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isVisible
                          ? 'text-green-700 bg-green-50 hover:bg-green-100'
                          : 'text-gray-500 bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {isVisible ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Show</>}
                    </button>
                    <button
                      onClick={() => deletePost(post.id, post.content)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-500 bg-red-50 text-xs font-medium hover:bg-red-100 transition-colors ml-auto"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Desktop table (hidden on mobile) ── */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Content</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Platform</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Status</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Scheduled</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Visible</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {posts.map(post => {
                  const scheduledDate = post.scheduledAt?.toDate?.();
                  const isVisible = post.visible !== false;
                  const status = post.status || 'draft';
                  const platform = post.platform || 'other';
                  return (
                    <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-800 max-w-xs truncate">
                          {post.content || '(no content)'}
                        </div>
                        {post.hashtags?.length > 0 && (
                          <div className="text-xs text-blue-400 truncate max-w-xs mt-0.5">
                            {post.hashtags.map(h => `#${h}`).join(' ')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${PLATFORM_COLORS[platform] ?? 'bg-gray-100 text-gray-600'}`}>
                          {platform}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-500'}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {scheduledDate ? format(scheduledDate, 'MMM d, yyyy HH:mm') : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                          {isVisible ? 'Visible' : 'Hidden'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/social/${post.id}/edit`)}
                            title="Edit"
                            className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => toggleVisible(post.id, isVisible)}
                            title={isVisible ? 'Hide' : 'Show'}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isVisible
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                          >
                            {isVisible ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                          <button
                            onClick={() => deletePost(post.id, post.content)}
                            title="Delete"
                            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
